import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import z from 'zod'
import { generateRequiredPaginationQuery, generateRequiredPaginationResult } from '../../shared/types'
import { ActorTypes, EventCategories } from './types'

extendZodWithOpenApi(z)

const WithDisplayText = z.object({ displayText: z.string().optional() }).catchall(z.unknown())

export const GetUserEventPathParamSchema = z.object({
  userId: z.string().uuid(),
})

export type GetUserEventPathParam = z.infer<typeof GetUserEventPathParamSchema>

export const GetUserEventQuerySchema = z.object({
  category: z.enum(EventCategories).optional(),
}).extend(generateRequiredPaginationQuery().shape)

export type GetUserEventQuery = z.infer<typeof GetUserEventQuerySchema>

export const GetUserEventResponseSchema = generateRequiredPaginationResult(z.object({
  userId: z.string().uuid(),
  category: z.enum(EventCategories),
  idempotentKey: z.string(),
  // Why isn't this a key of user-event-client event registry names? it will create a contract ciruclar dependency. We also cant move it into contracts as its core that package
  action: z.string(),
  actorType: z.enum(ActorTypes),
  actorId: z.string().uuid().nullable(),
  subscriptionId: z.string().uuid().nullable(),
  animalId: z.string().uuid().nullable(),
  orderId: z.string().uuid().nullable(),
  previous: WithDisplayText.nullable(),
  current: WithDisplayText.nullable(),
  payload: z.record(z.unknown()),
  displayText: z.string().max(727).nullable(),
  performedAt: z.string().datetime(),
}))

export type GetUserEventResponse = z.infer<typeof GetUserEventResponseSchema>

// Migration schemas for Laravel user events

// Payload schema for laravel_user_event - displayText is required
const LaravelEventPayloadSchema = z.object({
  displayText: z.string().describe('Pre-computed display text from Laravel'),
}).passthrough()

// Single event schema for migration request
export const MigrateUserEventSchema = z.object({
  id: z.string().uuid().describe('Deterministic UUID from Laravel for e2e tracking'),
  userId: z.string().uuid(),
  category: z.enum(EventCategories),
  idempotentKey: z.string(),
  action: z.literal('laravel_user_event'),
  actorType: z.enum(ActorTypes),
  actorId: z.string().uuid().nullable().optional(),
  subscriptionId: z.string().uuid().nullable().optional(),
  animalId: z.string().uuid().nullable().optional(),
  orderId: z.string().uuid().nullable().optional(),
  previous: WithDisplayText.nullable().optional(),
  current: WithDisplayText.nullable().optional(),
  payload: LaravelEventPayloadSchema,
  performedAt: z.string().datetime(),
})

export type MigrateUserEvent = z.infer<typeof MigrateUserEventSchema>

// Batch request body
export const MigrateUserEventsRequestSchema = z.object({
  dryRun: z.boolean().default(false),
  events: z.array(MigrateUserEventSchema).min(1),
})

export type MigrateUserEventsRequest = z.infer<typeof MigrateUserEventsRequestSchema>

// Response item schema - includes the UUID id from the request
export const MigrateUserEventResponseItemSchema = z.object({
  id: z.string().uuid().describe('UUID from the migration request'),
  userId: z.string().uuid(),
  category: z.enum(EventCategories),
  idempotentKey: z.string(),
  action: z.string(),
  actorType: z.enum(ActorTypes),
  actorId: z.string().uuid().nullable(),
  subscriptionId: z.string().uuid().nullable(),
  animalId: z.string().uuid().nullable(),
  orderId: z.string().uuid().nullable(),
  previous: WithDisplayText.nullable(),
  current: WithDisplayText.nullable(),
  payload: z.record(z.unknown()),
  displayText: z.string().max(727).nullable(),
  performedAt: z.string().datetime(),
})

export type MigrateUserEventResponseItem = z.infer<typeof MigrateUserEventResponseItemSchema>

// Response schema - array of migrated events with IDs
export const MigrateUserEventsResponseSchema = z.array(MigrateUserEventResponseItemSchema)

export type MigrateUserEventsResponse = z.infer<typeof MigrateUserEventsResponseSchema>

// Request schema for verification
export const VerifyUserEventsRequestSchema = z.object({
  userId: z.string().uuid(),
  numberOfEvents: z.number().int().nonnegative(),
  startDate: z.string().date().optional().describe('The date to start migrating from'),
  endDate: z.string().date().optional().describe('The date to end migrating from'),
})

export type VerifyUserEventsRequest = z.infer<typeof VerifyUserEventsRequestSchema>

// Mismatch detail schema
export const VerifyMismatchSchema = z.object({
  field: z.string(),
  position: z.enum(['first', 'last', 'count']),
  expected: z.unknown(),
  actual: z.unknown(),
})

export type VerifyMismatch = z.infer<typeof VerifyMismatchSchema>

// Response schema for verification
export const VerifyUserEventsResponseSchema = z.object({
  verified: z.boolean(),
  mismatchCount: z.number().int().nonnegative(),
  mismatches: z.array(VerifyMismatchSchema),
})

export type VerifyUserEventsResponse = z.infer<typeof VerifyUserEventsResponseSchema>
// Delete migration schemas

// Request schema for deleting migrated user events
export const DeleteMigratedUserEventsRequestSchema = z.object({
  userIds: z.array(z.string().uuid()).min(1).max(100).describe('Array of user IDs to delete events for'),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().describe('Optional start date (YYYY-MM-DD), inclusive from 00:00:00 UTC'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional().describe('Optional end date (YYYY-MM-DD), inclusive through 23:59:59.999 UTC'),
  dryRun: z.boolean().default(false).describe('If true, returns count without deleting'),
})

export type DeleteMigratedUserEventsRequest = z.infer<typeof DeleteMigratedUserEventsRequestSchema>

// Response schema for delete operation
export const DeleteMigratedUserEventsResponseSchema = z.object({
  deletedCount: z.number().describe('Number of records deleted (or would be deleted in dry run)'),
  dryRun: z.boolean().describe('Whether this was a dry run'),
})

export type DeleteMigratedUserEventsResponse = z.infer<typeof DeleteMigratedUserEventsResponseSchema>
